import React, { useEffect, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { apiFetch } from '../utils/apiFetch';
import { API_BASE } from '../utils/config';

interface Address {
  id: string;
  name: string;
  address_line1: string;
  address_line2?: string;
  city: string;
  state: string;
  postal_code: string;
  country: string;
  mobile: string;
  is_primary?: boolean;
  deleted?: boolean;
}

interface CustomerData {
  id: string;
  first_name: string;
  last_name: string;
  email: string;
  phone_number: string;
  country_code?: string;
  addresses: Address[];
}

const EMPTY_ADDR: Omit<Address, 'id'> = {
  name: '', address_line1: '', address_line2: '', city: '',
  state: '', postal_code: '', country: 'India', mobile: '', is_primary: false,
};

const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const NAV_ITEMS = [
  {
    label: 'Profile', to: '/profile',
    icon: (
      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
        <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" /><circle cx="12" cy="7" r="4" />
      </svg>
    ),
  },
  {
    label: 'Orders', to: '/orders',
    icon: (
      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
        <path d="M6 2L3 6v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2V6l-3-4z" /><line x1="3" y1="6" x2="21" y2="6" /><path d="M16 10a4 4 0 0 1-8 0" />
      </svg>
    ),
  },
  {
    label: 'Wishlist', to: '/wishlist',
    icon: (
      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
        <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z" />
      </svg>
    ),
  },
];

const ProfilePage: React.FC = () => {
  const { user, openLogin } = useAuth();
    const { pathname } = useLocation();

  const [customer, setCustomer] = useState<CustomerData | null>(null);
  const [loading, setLoading] = useState(true);

  // Profile edit
  const [profileModal, setProfileModal] = useState(false);
  const [profileForm, setProfileForm] = useState({ first_name: '', last_name: '', email: '' });
  const [profileErrors, setProfileErrors] = useState<Record<string, string>>({});
  const [savingProfile, setSavingProfile] = useState(false);

  // Address
  const [addrModal, setAddrModal] = useState(false);
  const [editAddrId, setEditAddrId] = useState<string | null>(null);
  const [addrForm, setAddrForm] = useState<Omit<Address, 'id'>>({ ...EMPTY_ADDR });
  const [addrErrors, setAddrErrors] = useState<Record<string, string>>({});
  const [savingAddr, setSavingAddr] = useState(false);
  const [deletingId, setDeletingId] = useState<string | null>(null);

  const [toast, setToast] = useState<{ msg: string; type: 'success' | 'error' } | null>(null);

  useEffect(() => {
    if (!user) { openLogin(); return; }
    fetchCustomer();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const showToast = (msg: string, type: 'success' | 'error' = 'success') => {
    setToast({ msg, type });
    setTimeout(() => setToast(null), 3000);
  };

  const fetchCustomer = async () => {
    setLoading(true);
    try {
      const res = await apiFetch(`${API_BASE}/customers/${user!.userId}`);
      if (!res.ok) throw new Error();
      const data = await res.json();
      setCustomer(data.customer);
    } catch {
      showToast('Failed to load profile', 'error');
    } finally {
      setLoading(false);
    }
  };

  // ── Profile edit ────────────────────────────────────────────────────────────
  const openProfileModal = () => {
    if (!customer) return;
    setProfileForm({ first_name: customer.first_name, last_name: customer.last_name, email: customer.email });
    setProfileErrors({});
    setProfileModal(true);
  };

  const validateProfile = () => {
    const errs: Record<string, string> = {};
    if (!profileForm.first_name.trim()) errs.first_name = 'First name is required';
    if (!profileForm.last_name.trim()) errs.last_name = 'Last name is required';
    if (!profileForm.email.trim()) errs.email = 'Email is required';
    else if (!emailRegex.test(profileForm.email)) errs.email = 'Enter a valid email';
    setProfileErrors(errs);
    return Object.keys(errs).length === 0;
  };

  const handleSaveProfile = async () => {
    if (!validateProfile()) return;
    setSavingProfile(true);
    try {
      const res = await apiFetch(`${API_BASE}/customers/${user!.userId}`, {
        method: 'PUT',
        body: JSON.stringify(profileForm),
      });
      if (!res.ok) throw new Error();
      await fetchCustomer();
      setProfileModal(false);
      showToast('Profile updated successfully');
    } catch {
      showToast('Failed to update profile', 'error');
    } finally {
      setSavingProfile(false);
    }
  };

  // ── Address management ──────────────────────────────────────────────────────
  const openAddAddr = () => {
    setEditAddrId(null);
    setAddrForm({ ...EMPTY_ADDR });
    setAddrErrors({});
    setAddrModal(true);
  };

  const openEditAddr = (addr: Address) => {
    setEditAddrId(addr.id);
    setAddrForm({
      name: addr.name, address_line1: addr.address_line1,
      address_line2: addr.address_line2 || '', city: addr.city,
      state: addr.state, postal_code: String(addr.postal_code || ''),
      country: addr.country || 'India', mobile: String(addr.mobile || ''),
      is_primary: addr.is_primary || false,
    });
    setAddrErrors({});
    setAddrModal(true);
  };

  const validateAddr = () => {
    const errs: Record<string, string> = {};
    if (!addrForm.name.trim()) errs.name = 'Name is required';
    if (!addrForm.address_line1.trim()) errs.address_line1 = 'Address is required';
    if (!addrForm.city.trim()) errs.city = 'City is required';
    if (!addrForm.state.trim()) errs.state = 'State is required';
    if (!String(addrForm.postal_code || '').trim()) errs.postal_code = 'Postal code is required';
    if (!String(addrForm.country || '').trim()) errs.country = 'Country is required';
    if (!addrForm.mobile || !/^\d{10}$/.test(addrForm.mobile)) errs.mobile = 'Valid 10-digit mobile required';
    setAddrErrors(errs);
    return Object.keys(errs).length === 0;
  };

  const handleSaveAddr = async () => {
    if (!validateAddr() || !customer) return;
    setSavingAddr(true);
    try {
      let updated = customer.addresses ? [...customer.addresses] : [];
      if (editAddrId) {
        updated = updated.map((a) => (a.id === editAddrId ? { ...a, ...addrForm } : a));
      } else {
        updated.push({ id: '', ...addrForm });
      }
      const res = await apiFetch(`${API_BASE}/customers/${user!.userId}`, {
        method: 'PUT',
        body: JSON.stringify({ addresses: updated }),
      });
      if (!res.ok) throw new Error();
      await fetchCustomer();
      setAddrModal(false);
      showToast(editAddrId ? 'Address updated' : 'Address added');
    } catch {
      showToast('Failed to save address', 'error');
    } finally {
      setSavingAddr(false);
    }
  };

  const handleDeleteAddr = async (id: string) => {
    if (!customer) return;
    setDeletingId(id);
    try {
      const payload = {
        addresses: (customer.addresses || []).map((a) =>
          a.id === id ? { ...a, deleted: true } : a
        ),
      };
      const res = await apiFetch(`${API_BASE}/customers/${user!.userId}`, {
        method: 'PUT',
        body: JSON.stringify(payload),
      });
      if (!res.ok) throw new Error();
      await fetchCustomer();
      showToast('Address deleted');
    } catch {
      showToast('Failed to delete address', 'error');
    } finally {
      setDeletingId(null);
    }
  };

  if (!user) return null;

  const initials = customer
    ? `${customer.first_name?.[0] || ''}${customer.last_name?.[0] || ''}`.toUpperCase()
    : '?';

  return (
    <div className="pf-layout">

      {/* ── Sidebar ─────────────────────────────────────────────────────────── */}
      <aside className="pf-sidebar">
        {/* Avatar block */}
        <div className="pf-sidebar__avatar-block">
          <div className="pf-sidebar__avatar">{initials}</div>
          {loading ? (
            <div className="pf-sidebar__name" style={{ opacity: 0.5 }}>Loading…</div>
          ) : (
            <>
              <div className="pf-sidebar__name">{customer?.first_name} {customer?.last_name}</div>
              <div className="pf-sidebar__meta">{customer?.email}</div>
              {customer?.phone_number && (
                <div className="pf-sidebar__meta">+{customer.country_code || '91'} {customer.phone_number}</div>
              )}
            </>
          )}
        </div>

        {/* Nav links */}
        <nav className="pf-sidebar__nav">
          {NAV_ITEMS.map(({ label, to, icon }) => (
            <Link
              key={label}
              to={to}
              className={`pf-sidebar__link${pathname === to ? ' pf-sidebar__link--active' : ''}`}
            >
              {icon}
              {label}
            </Link>
          ))}
        </nav>
      </aside>

      {/* ── Main content ────────────────────────────────────────────────────── */}
      <main className="pf-main">
        {/* Toast */}
        {toast && (
          <div className={`profile-toast profile-toast--${toast.type}`}>{toast.msg}</div>
        )}

        <h1 className="pf-main__title">Profile Information</h1>

        {loading ? (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
            <div className="skeleton" style={{ height: 130, borderRadius: 14 }} />
            <div className="skeleton" style={{ height: 200, borderRadius: 14 }} />
          </div>
        ) : (
          <>
            {/* ── Personal Details card ──────────────────────────────────── */}
            <div className="pf-card" style={{ marginBottom: 24 }}>
              <div className="pf-card__header">
                <span className="pf-card__section-label">Personal Details</span>
                <button className="pf-btn-primary pf-btn--sm" onClick={openProfileModal}>
                  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
                    <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" />
                  </svg>
                  Edit
                </button>
              </div>

              <div className="pf-detail-rows">
                {/* Email */}
                <div className="pf-detail-row">
                  <div className="pf-detail-row__icon-wrap">
                    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z" /><polyline points="22,6 12,13 2,6" />
                    </svg>
                  </div>
                  <div>
                    <span className="pf-detail-row__label">Email</span>
                    <span className="pf-detail-row__value">{customer?.email || '—'}</span>
                  </div>
                </div>

                {/* Phone */}
                <div className="pf-detail-row">
                  <div className="pf-detail-row__icon-wrap">
                    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07A19.5 19.5 0 0 1 4.69 12 19.79 19.79 0 0 1 1.62 3.38 2 2 0 0 1 3.6 1.21h3a2 2 0 0 1 2 1.72c.127.96.361 1.903.7 2.81a2 2 0 0 1-.45 2.11L7.91 8.75a16 16 0 0 0 7.34 7.34l.96-.96a2 2 0 0 1 2.11-.45c.907.339 1.85.573 2.81.7A2 2 0 0 1 22 16.92z" />
                    </svg>
                  </div>
                  <div>
                    <span className="pf-detail-row__label">Phone</span>
                    <span className="pf-detail-row__value">+{customer?.country_code || '91'} {customer?.phone_number || '—'}</span>
                  </div>
                </div>
              </div>
            </div>

            {/* ── Saved Addresses ────────────────────────────────────────── */}
            <div className="pf-addresses-header">
              <span className="pf-addresses-title">Saved Addresses</span>
              <button className="pf-btn-primary pf-btn--sm" onClick={openAddAddr}>
                <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <line x1="12" y1="5" x2="12" y2="19" /><line x1="5" y1="12" x2="19" y2="12" />
                </svg>
                Add New Address
              </button>
            </div>

            <div className="pf-addr-list">
              {(!customer?.addresses || customer.addresses.length === 0) ? (
                <div className="pf-addr-empty">
                  <svg width="30" height="30" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                    <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z" /><circle cx="12" cy="10" r="3" />
                  </svg>
                  <p>No saved addresses available.</p>
                </div>
              ) : (
                customer.addresses.map((addr) => (
                  <div key={addr.id} className={`pf-addr-card${addr.is_primary ? ' pf-addr-card--primary' : ''}`}>
                    {/* Pin icon */}
                    <div className="pf-addr-card__pin">
                      <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z" /><circle cx="12" cy="10" r="3" />
                      </svg>
                    </div>

                    {/* Body */}
                    <div className="pf-addr-card__body">
                      <div className="pf-addr-card__name-row">
                        <span className="pf-addr-card__name">{addr.name}</span>
                        {addr.is_primary && <span className="pf-addr-card__badge">Primary</span>}
                      </div>
                      <p className="pf-addr-card__text">
                        {addr.address_line1}{addr.address_line2 ? `, ${addr.address_line2}` : ''}<br />
                        {addr.city}, {addr.state} – {addr.postal_code}<br />
                        {addr.country}
                      </p>
                      <div className="pf-addr-card__phone">
                        <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                          <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07A19.5 19.5 0 0 1 4.69 12 19.79 19.79 0 0 1 1.62 3.38 2 2 0 0 1 3.6 1.21h3a2 2 0 0 1 2 1.72c.127.96.361 1.903.7 2.81a2 2 0 0 1-.45 2.11L7.91 8.75a16 16 0 0 0 7.34 7.34l.96-.96a2 2 0 0 1 2.11-.45c.907.339 1.85.573 2.81.7A2 2 0 0 1 22 16.92z" />
                        </svg>
                        {addr.mobile}
                      </div>
                    </div>

                    {/* Actions */}
                    <div className="pf-addr-card__actions">
                      <button className="pf-addr-card__action-btn pf-addr-card__action-btn--edit" onClick={() => openEditAddr(addr)} title="Edit">
                        <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                          <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
                          <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" />
                        </svg>
                      </button>
                      <button
                        className="pf-addr-card__action-btn pf-addr-card__action-btn--del"
                        onClick={() => handleDeleteAddr(addr.id)}
                        disabled={deletingId === addr.id}
                        title="Delete"
                      >
                        {deletingId === addr.id
                          ? <span style={{ fontSize: 10 }}>…</span>
                          : (
                            <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                              <polyline points="3 6 5 6 21 6" />
                              <path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6" />
                              <path d="M10 11v6" /><path d="M14 11v6" />
                              <path d="M9 6V4a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2" />
                            </svg>
                          )}
                      </button>
                    </div>
                  </div>
                ))
              )}
            </div>
          </>
        )}
      </main>

      {/* ── Edit Profile Modal ─────────────────────────────────────────────── */}
      {profileModal && (
        <div className="addr-modal-overlay" onClick={() => setProfileModal(false)}>
          <div className="addr-modal" onClick={(e) => e.stopPropagation()}>
            <h3 className="addr-modal__title">Edit Profile</h3>
            <div className="addr-form">
              <div className="addr-form__field">
                <label>First Name</label>
                <input value={profileForm.first_name} onChange={(e) => setProfileForm((f) => ({ ...f, first_name: e.target.value }))} placeholder="First Name" className={profileErrors.first_name ? 'addr-input--error' : ''} />
                {profileErrors.first_name && <span className="addr-form__error">{profileErrors.first_name}</span>}
              </div>
              <div className="addr-form__field">
                <label>Last Name</label>
                <input value={profileForm.last_name} onChange={(e) => setProfileForm((f) => ({ ...f, last_name: e.target.value }))} placeholder="Last Name" className={profileErrors.last_name ? 'addr-input--error' : ''} />
                {profileErrors.last_name && <span className="addr-form__error">{profileErrors.last_name}</span>}
              </div>
              <div className="addr-form__field">
                <label>Email</label>
                <input type="email" value={profileForm.email} onChange={(e) => setProfileForm((f) => ({ ...f, email: e.target.value }))} placeholder="Email" className={profileErrors.email ? 'addr-input--error' : ''} />
                {profileErrors.email && <span className="addr-form__error">{profileErrors.email}</span>}
              </div>
            </div>
            <div className="addr-modal__actions">
              <button className="btn btn--outline" onClick={() => setProfileModal(false)}>Cancel</button>
              <button className="btn btn--primary" onClick={handleSaveProfile} disabled={savingProfile}>
                {savingProfile ? 'Saving…' : 'Save Changes'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Add / Edit Address Modal ───────────────────────────────────────── */}
      {addrModal && (
        <div className="addr-modal-overlay" onClick={() => setAddrModal(false)}>
          <div className="addr-modal" onClick={(e) => e.stopPropagation()}>
            <h3 className="addr-modal__title">{editAddrId ? 'Edit Address' : 'Add New Address'}</h3>
            <div className="addr-form">
              <div className="addr-form__field">
                <label>Name</label>
                <input value={addrForm.name} onChange={(e) => setAddrForm((f) => ({ ...f, name: e.target.value }))} placeholder="Full Name" className={addrErrors.name ? 'addr-input--error' : ''} />
                {addrErrors.name && <span className="addr-form__error">{addrErrors.name}</span>}
              </div>
              <div className="addr-form__field">
                <label>Address Line 1</label>
                <input value={addrForm.address_line1} onChange={(e) => setAddrForm((f) => ({ ...f, address_line1: e.target.value }))} placeholder="Street, Building, No." className={addrErrors.address_line1 ? 'addr-input--error' : ''} />
                {addrErrors.address_line1 && <span className="addr-form__error">{addrErrors.address_line1}</span>}
              </div>
              <div className="addr-form__field">
                <label>Address Line 2 (Optional)</label>
                <input value={addrForm.address_line2 || ''} onChange={(e) => setAddrForm((f) => ({ ...f, address_line2: e.target.value }))} placeholder="Landmark, Area" />
              </div>
              <div className="addr-form__row">
                <div className="addr-form__field">
                  <label>City</label>
                  <input value={addrForm.city} onChange={(e) => setAddrForm((f) => ({ ...f, city: e.target.value }))} placeholder="City" className={addrErrors.city ? 'addr-input--error' : ''} />
                  {addrErrors.city && <span className="addr-form__error">{addrErrors.city}</span>}
                </div>
                <div className="addr-form__field">
                  <label>State</label>
                  <input value={addrForm.state} onChange={(e) => setAddrForm((f) => ({ ...f, state: e.target.value }))} placeholder="State" className={addrErrors.state ? 'addr-input--error' : ''} />
                  {addrErrors.state && <span className="addr-form__error">{addrErrors.state}</span>}
                </div>
              </div>
              <div className="addr-form__row">
                <div className="addr-form__field">
                  <label>Postal Code</label>
                  <input value={addrForm.postal_code} onChange={(e) => setAddrForm((f) => ({ ...f, postal_code: e.target.value }))} placeholder="6-digit PIN" className={addrErrors.postal_code ? 'addr-input--error' : ''} />
                  {addrErrors.postal_code && <span className="addr-form__error">{addrErrors.postal_code}</span>}
                </div>
                <div className="addr-form__field">
                  <label>Country</label>
                  <input value={addrForm.country} onChange={(e) => setAddrForm((f) => ({ ...f, country: e.target.value }))} placeholder="Country" className={addrErrors.country ? 'addr-input--error' : ''} />
                  {addrErrors.country && <span className="addr-form__error">{addrErrors.country}</span>}
                </div>
              </div>
              <div className="addr-form__field">
                <label>Mobile</label>
                <input value={addrForm.mobile} onChange={(e) => setAddrForm((f) => ({ ...f, mobile: e.target.value }))} placeholder="10-digit number" className={addrErrors.mobile ? 'addr-input--error' : ''} />
                {addrErrors.mobile && <span className="addr-form__error">{addrErrors.mobile}</span>}
              </div>
            </div>
            <div className="addr-modal__actions">
              <button className="btn btn--outline" onClick={() => setAddrModal(false)}>Cancel</button>
              <button className="btn btn--primary" onClick={handleSaveAddr} disabled={savingAddr}>
                {savingAddr ? (editAddrId ? 'Saving…' : 'Adding…') : editAddrId ? 'Save Changes' : 'Add Address'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ProfilePage;
